import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Nav, Badge, Alert, Form, Card } from 'react-bootstrap';
import { FaCheckCircle, FaFilePdf, FaUpload } from 'react-icons/fa';
import { logoutUser, refreshCurrentUser } from '../store/slices/authSlice';
import {
  fetchDriverDocuments,
  fetchDriverOrders,
  acceptOrder,
  setActiveOrder,
  uploadDriverDocument,
} from '../store/slices/driverSlice';
import WalletGate from '../components/driver/WalletGate';
import { fetchWallet } from '../store/slices/walletSlice';
import { Link } from 'react-router-dom';
import DriverProfileDetails from '../components/driver/DriverProfileDetails';
import DriverOrderList from '../components/driver/DriverOrderList';
import ActiveDelivery from '../components/driver/ActiveDelivery';
import AppTopbar from '../components/AppTopbar';
import { APP_NAME, ROUTES } from '../config/appConfig';
import {
  DRIVER_DOCUMENT_TYPES,
  getDriverApprovalStatus,
  hasAllDriverDocuments,
} from '../utils/driverDocuments';
import '../styles/DriverDashboard.css';
import DriverTripHistory from '../components/driver/DriverTripHistory';
import useDriverPolling from '../utils/useDriverPolling';
import api from '../config/axiosConfig';
import DriverOnboarding from '../components/driver/DriverOnboarding';
import DriverSetupProgress from '../components/driver/DriverSetupProgress';

const ACTIVE_DRIVER_ORDER_STATUSES = ['ASSIGNED', 'PICKED_UP', 'IN_TRANSIT', 'OUT_FOR_DELIVERY'];
const needsDriverAction = order => ACTIVE_DRIVER_ORDER_STATUSES.includes(order?.status) || (order?.status === 'DELIVERED' && Number(order.cashDue) > 0);

const DriverDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, user, sessionRefreshing } = useSelector((state) => state.auth);
  const wallet = useSelector(state=>state.wallet);
  const {
    orders,
    activeOrder,
    documents: driverDocuments,
    documentReview,
    documentsLoaded,
    documentsLoading,
    documentsSubmitting,
  } = useSelector((state) => state.driver);
  useEffect(() => { if (isAuthenticated) dispatch(fetchWallet()); }, [dispatch, isAuthenticated,user?.id,user?.driverId,user?.userId]);
  const [activeTab, setActiveTab] = useState('orders');
  const acceptingOrder = useRef(false);
  const [acceptError, setAcceptError] = useState('');
  const locationWarning = 'Keep this tab open while online. Browser tracking cannot guarantee background delivery updates.';
  const [availability,setAvailability]=useState(null),[availabilityBusy,setAvailabilityBusy]=useState(false),[availabilityError,setAvailabilityError]=useState('');
  useEffect(()=>{if(!isAuthenticated)return;let active=true;api.get('/driver/availability').then(response=>{if(active)setAvailability(response.data.data);}).catch(()=>{});return()=>{active=false;};},[isAuthenticated]);
  useEffect(()=>{if(!availability?.online||!availability.sessionId)return;const timer=setInterval(()=>{api.post('/driver/tracking/sessions/'+encodeURIComponent(availability.sessionId)+'/heartbeat').catch(()=>setAvailability(current=>({...current,online:false,availability:'UNREACHABLE'})));},30000);return()=>clearInterval(timer);},[availability?.online,availability?.sessionId]);
  const toggleAvailability=async()=>{setAvailabilityBusy(true);setAvailabilityError('');try{await api.put('/driver/availability',{online:!availability?.online,platform:'WEB'},{headers:{'Idempotency-Key':'web-availability-'+Date.now()+'-'+Math.random().toString(36).slice(2)}});setAvailability((await api.get('/driver/availability')).data.data);}catch(error){setAvailabilityError(error.response?.data?.message||error.message);}finally{setAvailabilityBusy(false);}};
  const [approvalUnlockedNotice, setApprovalUnlockedNotice] = useState(false);
  const [documentUploadError, setDocumentUploadError] = useState('');
  const [draftDriverDocuments, setDraftDriverDocuments] = useState({});
  const [selectedDocumentFiles, setSelectedDocumentFiles] = useState({});
  const driverApproved = documentsLoaded && (documentReview?.approvalKnown ? documentReview.status === 'APPROVED' : getDriverApprovalStatus(user, true) === 'APPROVED');
  const effectiveDriverApproved = driverApproved && user?.phoneVerified === true;
  const [profileVehicleSaved, setProfileVehicleSaved] = useState(false);
  const detailsSaved = Boolean(profileVehicleSaved && user?.phoneVerified === true && String(user?.vehicleNumber || '').trim() && String(user?.licenseNumber || '').trim());
  const documentsComplete = hasAllDriverDocuments(driverDocuments);
  const hasSelectedDocumentFiles = Object.keys(selectedDocumentFiles).length > 0;
  const previousApprovalRef = useRef(effectiveDriverApproved);

  const account=String(user?.id??user?.driverId??user?.userId??'');
  const [setup,setSetup]=useState(null);
  const onEligibilityChange=useCallback(value=>setSetup(value?{account,...value}:null),[account]);
  const eligibility=setup?.account===account?setup:null;
  const queueReady=effectiveDriverApproved&&eligibility?.dispatchEligible===true&&wallet?.data?.canAcceptOrders===true&&!wallet.error&&availability?.online===true;
  const setupComplete=eligibility?.dispatchEligible===true||Boolean(eligibility?.dispatchBlockers?.length&&eligibility.dispatchBlockers.every(code=>code==='INSUFFICIENT_PREPAID_BALANCE'));
  const readyToGoOnline=effectiveDriverApproved&&eligibility?.dispatchEligible===true&&wallet?.data?.canAcceptOrders===true&&!wallet.error;
  const needsDocumentSetup=!effectiveDriverApproved||(eligibility?.dispatchBlockers||[]).some(code=>code.startsWith('DOCUMENT_'));
  const [deliveryStats, setDeliveryStats] = useState(null);
  useEffect(() => {
    if (!isAuthenticated || !effectiveDriverApproved || !account) return;
    let current = true;
    api.get('/driver/trips/history', { params: { page: 0, size: 1 } }).then(response => {
      const count = response.data?.data?.totalDelivered;
      if (response.data?.success !== true || !Number.isSafeInteger(count) || count < 0) throw new Error('Delivery count unavailable');
      if (current) setDeliveryStats({ account, count });
    }).catch(() => { if (current) setDeliveryStats({ account, error: true }); });
    return () => { current = false; };
  }, [account, isAuthenticated, effectiveDriverApproved, activeOrder?.status]);
  const deliveredCount = effectiveDriverApproved && deliveryStats?.account === account && !deliveryStats.error ? deliveryStats.count : '--';
  useDriverPolling(async()=>{await dispatch(fetchDriverOrders()).unwrap();},isAuthenticated&&effectiveDriverApproved&&Boolean((eligibility&&(eligibility.dispatchEligible||eligibility.currentAssignmentId))||activeOrder||orders.some(needsDriverAction)),5000,account);
  useDriverPolling(async()=>{await dispatch(fetchDriverDocuments()).unwrap();},isAuthenticated&&!effectiveDriverApproved,8000,account);
  useDriverPolling(async()=>{await dispatch(fetchWallet({force:true})).unwrap();},isAuthenticated,15000,account);
  useEffect(()=>{if(isAuthenticated&&effectiveDriverApproved)dispatch(fetchDriverDocuments());},[isAuthenticated,effectiveDriverApproved,dispatch]);
  useEffect(() => {
    if (!hasSelectedDocumentFiles && !documentsSubmitting) {
      setDraftDriverDocuments(driverDocuments);
    }
  }, [driverDocuments, documentsSubmitting, hasSelectedDocumentFiles]);

  useEffect(() => {
    if (!previousApprovalRef.current && effectiveDriverApproved) {
      setApprovalUnlockedNotice(true);
      setActiveTab('orders');
    }

    previousApprovalRef.current = effectiveDriverApproved;
  }, [effectiveDriverApproved]);

  useEffect(() => {
    if (!approvalUnlockedNotice) {
      return;
    }

    const timeout = setTimeout(() => {
      setApprovalUnlockedNotice(false);
    }, 5000);

    return () => clearTimeout(timeout);
  }, [approvalUnlockedNotice]);

  const handleLogout = async () => {
    await dispatch(logoutUser());
    navigate(ROUTES.driverLogin);
  };

  const handleSelectOrder = async (order) => {
    if (acceptingOrder.current) return;
    acceptingOrder.current = true;
    setAcceptError('');
    try {
      if (order.status === 'PENDING') {
        await dispatch(acceptOrder(order.id || order.orderId)).unwrap();
      } else {
        dispatch(setActiveOrder(order));
      }
      setActiveTab('active');
    } catch (error) {
      setAcceptError(typeof error === 'string' ? error : error?.message || 'Unable to accept this trip. Refresh orders and try again.');
    } finally {
      acceptingOrder.current = false;
    }
  };

  const isEmailVerificationError = (value) => (
    /EMAIL_NOT_VERIFIED|verify your email/i.test(String(value?.code || value?.error || value?.message || value || ''))
  );

  const handleDocumentUpload = (documentType, file) => {
    if (!detailsSaved) { setDocumentUploadError('Save your vehicle and driving license details before selecting documents.'); return; }
    if (!file) {
      return;
    }

    const isPdfFile = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

    if (!isPdfFile) {
      setDocumentUploadError('Only PDF documents are allowed. Please upload a PDF file.');
      return;
    }
    if (!file.size || file.size > 5 * 1024 * 1024) {
      setDocumentUploadError('Choose a non-empty PDF no larger than 5 MB.');
      return;
    }

    setDocumentUploadError('');
    setSelectedDocumentFiles((currentFiles) => ({
      ...currentFiles,
      [documentType]: file,
    }));
    setDraftDriverDocuments((currentDocuments) => ({
      ...currentDocuments,
      [documentType]: {
        name: file.name,
        type: file.type,
        size: file.size,
        file,
        uploadedAt: new Date().toISOString(),
      },
    }));
  };

  const handleSubmitDocuments = async () => {
    if (documentsSubmitting) return;
    if (!detailsSaved) { setDocumentUploadError('Save your vehicle and driving license details before submitting documents.'); return; }
    if (!DRIVER_DOCUMENT_TYPES.every(type => selectedDocumentFiles[type.key] || driverDocuments[type.key]?.name)) {
      setDocumentUploadError('Please upload Aadhar, DL, and vehicle RC before submitting.');
      return;
    }

    const documentsToUpload = DRIVER_DOCUMENT_TYPES.filter(
      (documentType) => selectedDocumentFiles[documentType.key]
    );

    if (documentsToUpload.length === 0) {
      setDocumentUploadError('Please choose at least one new document before submitting.');
      return;
    }

    try {
      setDocumentUploadError('');

      await dispatch(uploadDriverDocument(selectedDocumentFiles)).unwrap();

      await dispatch(fetchDriverDocuments()).unwrap();
      setSelectedDocumentFiles({});
    } catch (error) {
      setDocumentUploadError(
        isEmailVerificationError(error)
          ? 'Unable to submit documents. Please try again.'
          : error || 'Unable to submit documents. Please try again.'
      );
    }
  };

  const activeDriverOrder = activeOrder && needsDriverAction(activeOrder)
    ? activeOrder
    : orders.find((order) => needsDriverAction(order));
  const firstAvailableOrder = queueReady ? orders.find((order) => order.status === 'PENDING') : null;
  const visibleDriverOrder = activeDriverOrder || firstAvailableOrder || null;
  const visibleOrderList = visibleDriverOrder ? [visibleDriverOrder] : [];
  const activeOrders = activeDriverOrder ? [activeDriverOrder] : [];
  const driverCards = [
    {
      key: 'orders',
      eyebrow: 'Queue',
      title: 'Available Orders',
      description: 'See newly available pickups and choose what to start next.',
    },
    {
      key: 'active',
      eyebrow: 'In Progress',
      title: 'Current Delivery',
      description: 'Handle pickup OTP, navigation, and completion from one focused screen.',
    },
    {
      key: 'history',
      eyebrow: 'Completed',
      title: 'Delivery History',
      description: 'Review completed jobs without losing access to live work.',
    },
  ];

  return (
    <div className="driver-dashboard">
      <AppTopbar
        brandHref={ROUTES.driverDashboard}
        title={APP_NAME}
        subtitle="Driver Portal"
        navId="driver-navbar-nav"
        className="driver-navbar"
        pillText="Driver portal"
        pillClassName="driver-sync-pill"
        userText={[user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'Driver'}
        onLogout={handleLogout}
      />

      <Container className="py-4 py-lg-5"><nav aria-label="Driver payment pages" className="d-flex flex-wrap gap-3 mb-4">{['trips','earnings','ledger','payouts','support'].map(section=><Link key={section} to={'/driver/'+section}>{section.charAt(0).toUpperCase()+section.slice(1)}</Link>)}</nav>
        {!activeOrder&&!setupComplete&&<DriverSetupProgress profileSaved={profileVehicleSaved||effectiveDriverApproved} documentsSubmitted={documentsComplete&&!hasSelectedDocumentFiles} approved={effectiveDriverApproved} setup={eligibility} wallet={wallet}/>}
        <DriverOnboarding key={account} onEligibilityChange={onEligibilityChange} refreshKey={`${profileVehicleSaved}:${documentReview?.status}:${documentReview?.submissionStatus}:${wallet?.data?.balance}`}/>
        <Card className="mb-3"><Card.Body><p>Availability: {availability?.availability||'Unavailable'}</p><Button disabled={availabilityBusy||(!availability?.online&&(!eligibility?.dispatchEligible||wallet?.data?.canAcceptOrders!==true||!!wallet.error))} onClick={toggleAvailability}>{availability?.online?'Go offline':'Go online'}</Button>{availabilityError&&<Alert variant="danger">{availabilityError}</Alert>}</Card.Body></Card>
        {locationWarning && (
          <Alert variant="warning" className="mb-4">
            {locationWarning}
          </Alert>
        )}

        <p><Link to="/driver/notifications">Email preferences and history</Link></p>
        {approvalUnlockedNotice && (
          <Alert variant="success" className="mb-4 driver-approval-success">
            Your driver account has been approved. Complete driver setup and go online to receive orders.
            {documentReview?.reason ? ` Admin note: ${documentReview.reason}` : ''}
          </Alert>
        )}

        {!effectiveDriverApproved && (
          <Alert variant={documentReview?.status === 'REJECTED' ? 'danger' : 'warning'} className="mb-4 driver-approval-alert">
            <div className="driver-approval-alert-content">
              <div>
                <strong>{documentReview?.status === 'REJECTED' ? 'Approval rejected.' : 'Approval pending.'}</strong>{' '}
                {documentReview?.status === 'REJECTED'
                  ? `Admin rejected your documents${documentReview.reason ? `: ${documentReview.reason}` : '.'} Upload updated documents for another review.`
                  : documentsComplete ? 'Your documents have been submitted. Admin approval updates automatically; you can complete your city and safety training while waiting.' : 'Complete your vehicle details and upload the three required PDFs below.'}
              </div>
              <Button
                variant="outline-dark"
                size="sm"
                className="driver-approval-refresh-btn"
                onClick={() => {
                  dispatch(refreshCurrentUser());
                  dispatch(fetchDriverDocuments());
                }}
                disabled={sessionRefreshing || documentsLoading}
              >
                {sessionRefreshing || documentsLoading ? 'Checking...' : 'Check Now'}
              </Button>
            </div>
          </Alert>
        )}

        <section className="dashboard-hero driver-hero mb-4 mb-lg-5">
          <div>
            <span className="hero-kicker">Driver Workspace</span>
            <h1 className="hero-title">Stay focused from pickup to delivery.</h1>
            <p className="hero-subtitle">
              Accept orders faster, verify pickup securely, and complete deliveries with less screen clutter.
            </p>
          </div>
          <div className="hero-chip-row">
            <div className="hero-chip driver-chip">
              <span className="hero-chip-label">Pending</span>
              <strong>{effectiveDriverApproved ? visibleOrderList.length : '--'}</strong>
            </div>
            <div className="hero-chip driver-chip">
              <span className="hero-chip-label">Active</span>
              <strong>{effectiveDriverApproved ? activeOrders.length : '--'}</strong>
            </div>
            <div className="hero-chip driver-chip">
              <span className="hero-chip-label">Completed</span>
              <strong>{deliveredCount}</strong>
            </div>
          </div>
        </section>

        <Row className="g-3 mb-4">
          {driverCards.map((card) => (
            <Col md={4} key={card.key}>
              <button
                type="button"
                className={`dashboard-summary-card driver-summary-card ${activeTab === card.key ? 'is-active' : ''}`}
                onClick={() => setActiveTab(card.key)}
                disabled={!effectiveDriverApproved}
              >
                <span className="summary-eyebrow">{card.eyebrow}</span>
                <h3>{card.title}</h3>
                <p>{card.description}</p>
              </button>
            </Col>
          ))}
        </Row>

        <Row className="mb-4">
          <Col>
            <div className="section-heading">
              <h2>{activeTab === 'orders' ? 'Order Queue' : activeTab === 'active' ? 'Active Delivery Flow' : 'Completed Deliveries'}</h2>
              <p>
                {activeTab === 'orders'
                  ? 'Choose an available order and begin the delivery flow with fewer clicks.'
                  : activeTab === 'active'
                    ? 'Everything needed for the current job is grouped into one action-focused panel.'
                    : 'Review finished deliveries and your work summary.'}
              </p>
            </div>
          </Col>
        </Row>

        {/* Stats Row */}
        <Row className="mb-4 g-3">
          <Col xs={6} md={3}>
            <div className="stat-card pending">
              <h3>{effectiveDriverApproved ? visibleOrderList.length : '--'}</h3>
              <small>{effectiveDriverApproved ? 'Visible Order' : 'Approval Pending'}</small>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className="stat-card active">
              <h3>{effectiveDriverApproved ? activeOrders.length : '--'}</h3>
              <small>{effectiveDriverApproved ? 'In Transit' : 'Trips Locked'}</small>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className="stat-card total">
              <h3>{effectiveDriverApproved ? queueReady ? orders.length : activeOrders.length : '--'}</h3>
              <small>{effectiveDriverApproved ? 'Current Orders' : 'Await Review'}</small>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className="stat-card completed">
              <h3>{deliveredCount}</h3>
              <small>{effectiveDriverApproved ? 'Delivered' : 'No Access Yet'}</small>
              {effectiveDriverApproved && deliveryStats?.account === account && deliveryStats.error && <small role="alert">Could not load delivery count. Refresh to retry.</small>}
            </div>
          </Col>
        </Row>

        {effectiveDriverApproved&&<WalletGate />}{effectiveDriverApproved && <DriverProfileDetails onSavedChange={setProfileVehicleSaved} />}
        <Button as={Link} to={ROUTES.driverWallet} className="mb-3">Driver wallet / Add Money</Button>
        <Row>
          <Col lg={3} className="mb-3">
            <div className="nav-tabs-vertical dashboard-side-nav">
              <Nav className="flex-column gap-2">
                <Nav.Link
                  as="button"
                  className={`text-start ${activeTab === 'orders' ? 'active' : ''}`}
                  onClick={() => setActiveTab('orders')}
                  disabled={!effectiveDriverApproved}
                >
                  <span className="nav-tab-icon">📋</span>
                  <span className="nav-tab-copy">
                    <strong>Available Orders</strong>
                    <small>{effectiveDriverApproved ? 'Pickup queue' : 'Locked until approval'}</small>
                  </span>
                  {effectiveDriverApproved && visibleOrderList.length > 0 && (
                    <Badge bg="danger" className="ms-auto nav-tab-badge">{visibleOrderList.length}</Badge>
                  )}
                </Nav.Link>
                <Nav.Link
                  as="button"
                  className={`text-start ${activeTab === 'active' ? 'active' : ''}`}
                  onClick={() => setActiveTab('active')}
                  disabled={!effectiveDriverApproved}
                >
                  <span className="nav-tab-icon">🚚</span>
                  <span className="nav-tab-copy">
                    <strong>Active Delivery</strong>
                    <small>{effectiveDriverApproved ? 'Current job' : 'Locked until approval'}</small>
                  </span>
                  {effectiveDriverApproved && activeOrder && <Badge bg="success" className="ms-auto nav-tab-badge">1</Badge>}
                </Nav.Link>
                <Nav.Link
                  as="button"
                  className={`text-start ${activeTab === 'history' ? 'active' : ''}`}
                  onClick={() => setActiveTab('history')}
                  disabled={!effectiveDriverApproved}
                >
                  <span className="nav-tab-icon">📜</span>
                  <span className="nav-tab-copy">
                    <strong>Delivery History</strong>
                    <small>{effectiveDriverApproved ? 'Completed jobs' : 'Locked until approval'}</small>
                  </span>
                </Nav.Link>
              </Nav>
            </div>
          </Col>

          <Col lg={9}>
            <div className="content-area dashboard-panel">
              {needsDocumentSetup ? (
                <div id="driver-documents" className="driver-document-panel">
                  <div className="section-heading mb-4">
                    <h2>Upload Driver Documents</h2>
                    <p>Upload clear Aadhaar, driving licence and vehicle RC PDFs (up to 5 MB each). Submit them for admin review, then complete your city and safety training while waiting.</p>
                  </div>

                  <DriverProfileDetails onSavedChange={setProfileVehicleSaved} />
                  {!detailsSaved && <p className="text-muted">Save both details above to enable document upload.</p>}
                  {documentUploadError && <Alert variant="danger">{documentUploadError}</Alert>}
                  {needsDocumentSetup && (
                    <div className="driver-document-grid">
                      {DRIVER_DOCUMENT_TYPES.map((documentType) => {
                        const document = draftDriverDocuments[documentType.key];
                        const selectedFile = selectedDocumentFiles[documentType.key];
                        const inputId = `driver-document-${documentType.key}`;

                        return (
                          <div className="driver-document-card" key={documentType.key}>
                            <div className="driver-document-card-header">
                              <span className={`driver-document-icon ${document ? 'is-ready' : ''}`}>
                                {document ? <FaCheckCircle /> : <FaFilePdf />}
                              </span>
                              <div>
                                <span className="summary-eyebrow">{documentType.label}</span>
                                <h3>{document?.name || 'No PDF selected'}</h3>
                              </div>
                            </div>
                            <p>
                              {document?.uploadedAt
                                ? `Selected ${new Date(document.uploadedAt).toLocaleString()}`
                                : 'Upload a clear PDF document for review.'}
                            </p>
                            <label className="driver-document-upload" htmlFor={inputId}>
                              <FaUpload />
                              <span>{selectedFile || document ? 'Replace PDF' : 'Upload PDF'}</span>
                            </label>
                            <Form.Control
                              id={inputId}
                              className="driver-document-input"
                              type="file"
                              disabled={!detailsSaved || documentsSubmitting}
                              accept="application/pdf,.pdf"
                              onChange={(event) => {
                                handleDocumentUpload(documentType.key, event.target.files?.[0]);
                                event.target.value = '';
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="driver-document-status">
                    <Badge bg={documentsComplete && !hasSelectedDocumentFiles ? 'success' : 'warning'} text={documentsComplete && !hasSelectedDocumentFiles ? undefined : 'dark'}>
                      {hasSelectedDocumentFiles ? 'Changes ready to submit' : documentsComplete ? 'Submitted for admin review' : 'Not submitted'}
                    </Badge>
                    <span>
                      {documentReview?.status === 'REJECTED'
                        ? documentReview.reason || 'Admin requested updated documents.'
                        : documentsComplete && !hasSelectedDocumentFiles
                          ? 'Your documents are in review. We will let you know once your account is approved.'
                          : 'Upload all three documents to complete your review packet.'}
                    </span>
                  </div>
                  {(!documentsComplete || hasSelectedDocumentFiles) && (
                    <Button
                      variant="primary"
                      className="driver-document-submit"
                      disabled={!detailsSaved || !hasSelectedDocumentFiles || !DRIVER_DOCUMENT_TYPES.every(type => selectedDocumentFiles[type.key] || driverDocuments[type.key]?.name) || documentsSubmitting}
                      onClick={handleSubmitDocuments}
                    >
                      {documentsSubmitting ? 'Submitting...' : 'Submit Documents'}
                    </Button>
                  )}
                </div>
              ) : activeTab === 'orders' && !queueReady && !activeDriverOrder ? (
                readyToGoOnline ? <Alert variant="info"><h2 className="h5">You are offline</h2><p>Your setup is complete. Go online to receive available orders.</p><Button disabled={availabilityBusy} onClick={toggleAvailability}>{availabilityBusy?'Going online...':'Go online'}</Button>{availabilityError&&<p className="mt-2">{availabilityError}</p>}</Alert> : <Alert variant="warning"><h2 className="h5">{setupComplete?'Check your wallet':'Complete setup before viewing available orders'}</h2><p>{!eligibility?'Refresh driver setup to confirm your eligibility.':!setupComplete?'Complete the requirements listed in Driver setup above.':wallet?.error||!wallet?.data?'Refresh your wallet to confirm the balance.':'Add the required wallet balance before accepting orders.'}</p>{!setupComplete&&<Button as="a" href="#driver-setup" variant="outline-primary">Complete driver setup</Button>}{' '}<Button as={Link} to={ROUTES.driverWallet}>Open wallet</Button></Alert>
              ) : activeTab === 'orders' ? (
                <>{acceptError && <Alert variant="danger">{acceptError}</Alert>}<DriverOrderList
                  orders={visibleOrderList}
                  onSelectOrder={handleSelectOrder}
                /></>
              ) : activeTab === 'active' ? (
                <ActiveDelivery order={activeDriverOrder} onBack={() => { dispatch(setActiveOrder(null)); setActiveTab('orders'); }} />
              ) : (
                <DriverTripHistory/>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default DriverDashboard;
